import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const triggerStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 4,
  border: 0,
  borderRadius: 'var(--dcm-cms-help-radius, 4px)',
  background: 'var(--dcm-cms-help-trigger-background, transparent)',
  color: 'var(--dcm-cms-help-trigger-color, var(--puck-color-text-subtle, #646d72))',
  cursor: 'help',
};

const tooltipStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: 10000,
  padding: 10,
  boxSizing: 'border-box',
  border: '1px solid var(--dcm-cms-help-border, var(--puck-color-border, #d0d4d8))',
  borderRadius: 'var(--dcm-cms-help-radius, 4px)',
  background: 'var(--dcm-cms-help-background, var(--puck-color-surface, #fff))',
  color: 'var(--dcm-cms-help-text, var(--puck-color-text, #181b1f))',
  fontFamily: 'var(--dcm-cms-help-font-family, var(--puck-font-family, inherit))',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.4,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  overflowY: 'auto',
};

type HelpTooltipProps = {
  text: string;
  label: string;
  id?: string;
  /** Full field width, not the icon width: keeps the popover inside a narrow inspector. */
  boundsRef?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
};

/** Explanations only. Actionable warnings and validation errors must remain visible inline. */
export function HelpTooltip({ text, label, id, boundsRef, style }: HelpTooltipProps): JSX.Element {
  const generatedId = useId();
  const tooltipId = id ?? `cms-help-${generatedId}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const open = !dismissed && (hovered || focused || pinned);
  const ownerDocument = triggerRef.current?.ownerDocument;

  const dismiss = (): void => {
    setDismissed(true);
    setPinned(false);
    setHovered(false);
    setFocused(false);
  };

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    const view = trigger?.ownerDocument.defaultView;
    if (!open || !trigger || !tooltip || !view) return undefined;

    const updatePosition = (): void => {
      const anchor = trigger.getBoundingClientRect();
      const bounds = boundsRef?.current?.getBoundingClientRect();
      const leftEdge = Math.max(8, bounds?.left ?? 8);
      const rightEdge = Math.min(view.innerWidth - 8, bounds?.right ?? view.innerWidth - 8);
      const width = Math.max(0, Math.min(280, rightEdge - leftEdge));
      const left = Math.max(leftEdge, Math.min(anchor.left, rightEdge - width));
      // Wrapped text is taller than the shrink-to-fit box, so measure at the width it will have.
      tooltip.style.width = `${width}px`;
      const { height } = tooltip.getBoundingClientRect();
      const top = anchor.bottom + height > view.innerHeight - 8 ? Math.max(8, anchor.top - height) : anchor.bottom;
      setPosition({
        left,
        top,
        width,
        maxHeight: view.innerHeight - 16,
        fontFamily: `var(--dcm-cms-help-font-family, ${
          view.getComputedStyle(trigger.parentElement ?? trigger).fontFamily || 'sans-serif'
        })`,
      });
    };
    updatePosition();
    view.addEventListener('resize', updatePosition);
    view.addEventListener('scroll', updatePosition, true);
    return () => {
      view.removeEventListener('resize', updatePosition);
      view.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, boundsRef, text]);

  useEffect(() => {
    if (!open || !ownerDocument) return undefined;
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // Dismiss this explanation, not the dialog that contains its field.
      event.stopPropagation();
      dismiss();
    };
    const handleOutsidePress = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !tooltipRef.current?.contains(target)) dismiss();
    };
    ownerDocument.addEventListener('keydown', handleEscape, true);
    ownerDocument.addEventListener('mousedown', handleOutsidePress, true);
    ownerDocument.addEventListener('touchstart', handleOutsidePress, true);
    return () => {
      ownerDocument.removeEventListener('keydown', handleEscape, true);
      ownerDocument.removeEventListener('mousedown', handleOutsidePress, true);
      ownerDocument.removeEventListener('touchstart', handleOutsidePress, true);
    };
  }, [open, ownerDocument]);

  const handleMouseEnter = (): void => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
    setDismissed(false);
  };
  const handleMouseLeave = (): void => {
    leaveTimer.current = setTimeout(() => setHovered(false), 100);
  };

  const tooltip = (
    <span
      ref={tooltipRef}
      id={tooltipId}
      role='tooltip'
      hidden={!open}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ ...tooltipStyle, ...style, ...position }}
    >
      {text}
    </span>
  );

  return (
    <span style={{ display: 'inline-flex', flexShrink: 0 }}>
      <button
        ref={triggerRef}
        type='button'
        aria-label={`More info about ${label}`}
        aria-describedby={tooltipId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => {
          setFocused(false);
          setPinned(false);
        }}
        onClick={() => {
          if (pinned) dismiss();
          else {
            setPinned(true);
            setDismissed(false);
          }
        }}
        style={triggerStyle}
      >
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true' focusable='false'>
          <circle cx='8' cy='8' r='6.5' stroke='currentColor' />
          <path d='M8 7v4' stroke='currentColor' strokeWidth='1.5' />
          <circle cx='8' cy='4.5' r='.8' fill='currentColor' />
        </svg>
      </button>
      {open && ownerDocument ? createPortal(tooltip, ownerDocument.body) : tooltip}
    </span>
  );
}
